import { Button, IconButton, Stack, SxProps } from '@mui/material';
import { StyledTextField } from '..';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';

interface SearchFieldProps {
  sx?: SxProps;
  placeholder?: string;
  value?: string;
  startIcon?: ReactNode;
  includeEndIcon?: boolean;
  onTrigger: (value: string) => void;
}

export default function SearchField({
  placeholder,
  value,
  onTrigger,
  startIcon,
  includeEndIcon = false,
  sx
}: SearchFieldProps) {
  const theme = useTheme();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (value === undefined || value === null) return;
    setSearch(value);
  }, [value]);

  function onKeyPress(key: string) {
    if (key === 'Enter') {
      onTrigger(search);
    }
  }

  return (
    <Stack
      direction="row"
      sx={{
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: '12px',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        },
        '&:focus-within': {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`
        }
      }}>
      <StyledTextField
        placeholder={placeholder}
        startIcon={startIcon}
        endIcon={
          includeEndIcon ? (
            <IconButton
              onClick={() => {
                setSearch('');
                onTrigger('');
              }}
              sx={{
                fontSize: '1em',
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: theme.palette.error.main
                }
              }}>
              <CloseIcon />
            </IconButton>
          ) : (
            <></>
          )
        }
        value={search}
        onChange={setSearch}
        onKeyPress={onKeyPress}
        sx={{
          borderRadius: '12px 0 0 12px',
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: 'none'
            }
          },
          ...sx
        }}
      />
      <Button
        variant="contained"
        onClick={() => onTrigger(search)}
        sx={{
          fontSize: '1em',
          borderRadius: '0 12px 12px 0',
          minWidth: '48px',
          width: '48px',
          backgroundColor: theme.palette.primary.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            transform: 'translateY(-1px)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        }}>
        <ArrowForwardIcon sx={{ color: 'white' }} />
      </Button>
    </Stack>
  );
}
